import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './hooks/useAuth';
import Layout from './components/layout/Layout';
import Login from './pages/Login';
import Signup from './pages/Signup';
import AcceptInvitation from './pages/AcceptInvitation';
import AdminPanel from './pages/AdminPanel';
import OrgSelector from './pages/OrgSelector';
import Dashboard from './pages/Dashboard';
import Team from './pages/Team';
import Projects from './pages/Projects';
import TimeLogs from './pages/TimeLogs';
import Screenshots from './pages/Screenshots';
import Reports from './pages/Reports';
import Invoices from './pages/Invoices';
import Settings from './pages/Settings';
import WorkDiary from './pages/WorkDiary';
import Standups from './pages/Standups';
import Meetings from './pages/Meetings';
import EmailPreferences from './pages/EmailPreferences';
import SetPassword from './pages/SetPassword';
import Activity from './pages/Activity';
import Timeline from './pages/Timeline';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// ---- route guards ----

function RequireAuth({ children }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function RequireOrganization({ children }) {
  const { user, organization, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Check if Master Admin is viewing an organization
  const isMasterAdmin = user?.isMasterAdmin;
  const viewingOrgAsAdmin = localStorage.getItem('viewingOrgAsAdmin');

  // Master Admin with org selected OR regular user with organization
  const hasValidContext = (isMasterAdmin && viewingOrgAsAdmin) || (!isMasterAdmin && organization);

  if (!user || !hasValidContext) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error</h1>
          <p className="text-gray-600 mb-4">User is not assigned to any organization</p>
          <button onClick={() => window.location.href = '/login'} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
            Login Again
          </button>
        </div>
      </div>
    );
  }

  return children;
}

function RequireRole({ children, roles }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

// ---- app routes ----

function RootRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Master Admins go to admin panel, regular users to dashboard
  if (user?.isMasterAdmin) {
    return <Navigate to="/admin" replace />;
  }

  return <Navigate to="/dashboard" replace />;
}

function MasterAdminDashboardRedirect() {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // If Master Admin accessing /dashboard, show org selector
  if (user?.isMasterAdmin) {
    return <Navigate to="/org-selector" replace />;
  }

  // Regular users go to dashboard
  return <Navigate to="/dashboard" replace />;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route path="/set-password" element={<SetPassword />} />

      {/* Master Admin Panel */}
      <Route
        path="/admin"
        element={
          <RequireAuth>
            <AdminPanel />
          </RequireAuth>
        }
      />

      {/* Organization Selector (Master Admin viewing org dashboard) */}
      <Route
        path="/org-selector"
        element={
          <RequireAuth>
            <OrgSelector />
          </RequireAuth>
        }
      />

      {/* Root redirect */}
      <Route path="/" element={<RootRedirect />} />

      {/* Protected layout */}
      <Route
        element={
          <RequireAuth>
            <RequireOrganization>
              <Layout />
            </RequireOrganization>
          </RequireAuth>
        }
      >
        {/* All roles */}
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/projects" element={<Projects />} />
        <Route path="/timelogs" element={<TimeLogs />} />
        <Route path="/email-preferences" element={<EmailPreferences />} />

        {/* Admin + Manager */}
        <Route
          path="/team"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Team />
            </RequireRole>
          }
        />
        <Route
          path="/screenshots"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Screenshots />
            </RequireRole>
          }
        />
        <Route
          path="/work-diary"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <WorkDiary />
            </RequireRole>
          }
        />
        <Route
          path="/standups"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Standups />
            </RequireRole>
          }
        />
        <Route
          path="/meetings"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Meetings />
            </RequireRole>
          }
        />
        <Route
          path="/reports"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Reports />
            </RequireRole>
          }
        />
        <Route
          path="/activity"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Activity />
            </RequireRole>
          }
        />
        <Route
          path="/timeline"
          element={
            <RequireRole roles={['ADMIN', 'MANAGER']}>
              <Timeline />
            </RequireRole>
          }
        />

        {/* Admin only */}
        <Route
          path="/invoices"
          element={
            <RequireRole roles={['ADMIN']}>
              <Invoices />
            </RequireRole>
          }
        />
        <Route
          path="/settings"
          element={
            <RequireRole roles={['ADMIN']}>
              <Settings />
            </RequireRole>
          }
        />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
